import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, CloudArrowUp, ChartLineUp, Trash } from "@phosphor-icons/react";
import { http } from "@/lib/api";

const api = {
  rows: () => http.get("/locker").then((r) => r.data),
  lock: () => http.post("/locker/snapshot?label=manual").then((r) => r.data),
  entries: (file, dg) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post(`/locker/entries${dg ? `?dg=${dg}` : ""}`, fd,
      { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  results: (files, dg) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    return http.post(`/locker/results${dg ? `?dg=${dg}` : ""}`, fd,
      { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  grade: (dg) => http.post(`/locker/grade?dg=${dg}`).then((r) => r.data),
  clear: (dg) => http.delete(`/locker/${dg}`).then((r) => r.data),
};

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const num = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));

function Pill({ ok, children, testId }) {
  return (
    <span data-testid={testId}
      className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${ok
        ? "border-[#39FF14]/60 text-[#39FF14] bg-[#39FF14]/10"
        : "border-zinc-800 text-zinc-500"}`}>
      {children}
    </span>
  );
}

function SlateRow({ row, onChanged }) {
  const entryRef = useRef(null);
  const resultRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const dg = row.slate_dg;
  const g = row.grade;
  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); toast.success(ok(r), { duration: 8000 }); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work", { duration: 12000 }); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-zinc-800 bg-black/30 p-3" data-testid={`locker-row-${dg}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="mono text-[11px] text-white font-bold">{row.slate_label || `DG ${dg}`}</div>
          <div className="mono text-[9px] text-zinc-500">{row.slate_type || "—"} · dg {dg}</div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Pill ok={!!row.snapshot} testId={`locker-pill-snapshot-${dg}`}>
            {row.snapshot ? `locked ${when(row.snapshot.captured_at)}` : "not locked"}
          </Pill>
          <Pill ok={(row.builds || []).length > 0} testId={`locker-pill-builds-${dg}`}>
            {(row.builds || []).length
              ? `${(row.builds || []).reduce((s, b) => s + (b.n_lineups || 0), 0)} auto-saved`
              : "no build saved"}
          </Pill>
          <Pill ok={!!row.entries} testId={`locker-pill-entries-${dg}`}>
            {row.entries ? `${row.entries.n_entries} entries` : "no entries"}
          </Pill>
          <Pill ok={row.results.length > 0} testId={`locker-pill-results-${dg}`}>
            {row.results.length ? `${row.results.length} contest${row.results.length === 1 ? "" : "s"}` : "no results"}
          </Pill>
          <button type="button" onClick={() => run(() => api.clear(dg), () => `Slate ${dg} cleared from the locker`)}
            className="text-zinc-600 hover:text-[#FF3B30] px-1" title="Remove this slate from the locker"
            data-testid={`locker-clear-${dg}`}><Trash size={12} /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-3">
        <div>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">1 · pre-lock snapshot</div>
          <div className={`border px-2 py-1.5 mono text-[10px] ${row.snapshot
            ? "border-zinc-800 text-zinc-300" : "border-[#FF3B30]/50 text-[#FF3B30]"}`}
            data-testid={`locker-snapshot-cell-${dg}`}>
            {row.snapshot
              ? `${row.snapshot.n_projected} projected · ${when(row.snapshot.captured_at)}`
              : "never locked — this slate can't be graded"}
          </div>
        </div>
        <div>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
            2 · entries {row.entries ? "you uploaded" : "(optional)"}
          </div>
          <button type="button" disabled={busy} onClick={() => entryRef.current?.click()}
            data-testid={`locker-entries-upload-${dg}`}
            title="Optional — every build is auto-saved. Upload your DK entries CSV only if you hand-edited lineups before submitting; it then replaces the auto-saved build for grading."
            className="w-full border border-dashed border-zinc-700 hover:border-[#FFCC00] text-zinc-400 hover:text-white mono text-[10px] uppercase tracking-widest py-1.5 inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
            <CloudArrowUp size={11} /> {row.entries ? "Replace" : "Upload DK entries"}
          </button>
          <input ref={entryRef} type="file" accept=".csv,text/csv" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(() => api.entries(f, dg), (r) => `${r.n_entries} submitted entries stored`); }} />
          {!row.entries && (row.builds || []).length > 0 && (
            <div className="mono text-[9px] text-[#39FF14]/80 mt-1" data-testid={`locker-builds-cell-${dg}`}>
              {(row.builds || []).map((b) => `${b.source_label || b.source}: ${b.n_lineups} · ${when(b.captured_at)}`).join(" · ")}
            </div>
          )}
        </div>
        <div>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">3 · results</div>
          <button type="button" disabled={busy} onClick={() => resultRef.current?.click()}
            data-testid={`locker-results-upload-${dg}`}
            title="DK contest-standings exports — pick as many as you like, one per contest."
            className="w-full border border-dashed border-zinc-700 hover:border-[#39FF14] text-zinc-400 hover:text-[#39FF14] mono text-[10px] uppercase tracking-widest py-1.5 inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
            <CloudArrowUp size={11} /> {row.results.length ? "Add more" : "Upload standings"}
          </button>
          <input ref={resultRef} type="file" accept=".csv,.zip,text/csv,application/zip" multiple className="hidden"
            onChange={(e) => { const f = e.target.files; e.target.value = ""; if (f?.length) run(() => api.results(f, dg), (r) => `${r.loaded.length} contest${r.loaded.length === 1 ? "" : "s"} loaded${r.failed?.length ? ` · ${r.failed.length} rejected` : ""}`); }} />
        </div>
        <div>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">grade</div>
          <button type="button" disabled={busy || !row.snapshot || !row.results.length}
            onClick={() => run(() => api.grade(dg), () => "Graded — calibration updated")}
            data-testid={`locker-grade-${dg}`}
            title={!row.snapshot ? "This slate was never locked before kickoff" : !row.results.length ? "Upload the contest standings first" : "Grade this slate and feed the calibration"}
            className="w-full bg-[#FFCC00] hover:bg-[#e0b400] text-black font-black uppercase tracking-widest mono text-[10px] py-1.5 disabled:opacity-30 inline-flex items-center justify-center gap-1.5">
            <ChartLineUp size={11} /> {g ? "Re-grade" : "Grade slate"}
          </button>
        </div>
      </div>

      {g && (
        <div className="mt-3 border-t border-zinc-900 pt-2 grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid={`locker-grade-card-${dg}`}>
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">projection</div>
            <div className="mono text-[11px] text-white">MAE {num(g.projection?.mae)} · bias {num(g.projection?.bias)}</div>
            <div className="mono text-[9px] text-zinc-500">corr {num(g.projection?.corr, 3)} · {g.n_players_matched} players</div>
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">ownership</div>
            <div className="mono text-[11px] text-white">MAE {num(g.ownership?.mae)} · bias {num(g.ownership?.bias)}</div>
            {g.ownership?.market_mae != null && <div className="mono text-[9px] text-zinc-500">market MAE {num(g.ownership.market_mae)}</div>}
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
              {g.entries?.source === "auto_build" ? "auto-saved build" : "your entries"}
            </div>
            {g.entries?.available ? (
              <>
                <div className="mono text-[11px] text-white">best {num(g.entries.best_score)} pts · top {num(g.entries.best_finish_pct)}%</div>
                <div className="mono text-[9px] text-zinc-500">{g.entries.n_entries} graded · median {num(g.entries.median_finish_pct)}% · top-1% {g.entries.top1pct_entries}</div>
              </>
            ) : <div className="mono text-[9px] text-zinc-600">{g.entries?.reason || "—"}</div>}
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">most accurate site</div>
            {g.by_site ? (
              Object.entries(g.by_site).sort((a, b) => a[1].mae - b[1].mae).slice(0, 2).map(([k, v]) => (
                <div key={k} className="mono text-[10px] text-zinc-300">{k.replace(/_(proj|own)$/, " $1")} · MAE {num(v.mae)}</div>
              ))
            ) : <div className="mono text-[9px] text-zinc-600">no site files on this slate</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SlateLocker() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.rows().then(setData).catch(() => setData({ slates: [] }));
  useEffect(() => { load(); }, []);

  const lockNow = async () => {
    setBusy(true);
    try {
      const r = await api.lock();
      toast.success(`Locked ${r.slate_label} · ${r.n_projected} projected players frozen`, { duration: 8000 });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lock failed", { duration: 12000 }); }
    finally { setBusy(false); }
  };

  const rows = data?.slates || [];
  return (
    <div className="space-y-4" data-testid="slate-locker-page">
      <div className="bg-[#0a0a0a] border border-zinc-800 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline flex items-center gap-2"><Lock size={12} className="text-[#FFCC00]" /> Slate Locker</div>
            <h1 className="text-xl font-black text-white mt-1">Lock it, play it, grade it</h1>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              Every build — Auto-Sense, Smart or Manual — saves itself here: the slate is re-frozen with our
              projections and projected ownership as they stood, and the finished lineups are kept alongside it.
              All you have to do after the games is drop in the DK contest standings. The grade then feeds the
              projection model, the ownership temperature and the simulator's variance for every mode.
            </p>
          </div>
          <button type="button" onClick={lockNow} disabled={busy} data-testid="locker-lock-active"
            title="Re-freeze the active slate — do this after late news if you want the grade measured against your final numbers."
            className="border border-[#FFCC00]/60 text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black mono text-[10px] uppercase tracking-widest px-3 py-2 font-bold disabled:opacity-40">
            {busy ? "Locking…" : data?.active_locked ? "Re-lock active slate" : "Lock active slate"}
          </button>
        </div>
        {data && (
          <div className="mono text-[10px] text-zinc-500 mt-2" data-testid="locker-active-line">
            active: {data.active_slate_label || (data.active_slate_dg ? `DG ${data.active_slate_dg}` : "no slate applied")}
            {data.active_slate_type ? ` · ${data.active_slate_type}` : ""}
            {data.active_locked ? " · locked ✓" : " · not locked yet"}
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="border border-zinc-800 bg-black/30 p-6 text-center" data-testid="locker-empty">
          <div className="mono text-[11px] text-zinc-400">Nothing locked yet.</div>
          <div className="mono text-[10px] text-zinc-600 mt-1">
            Build a portfolio in Auto-Sense, Smart or Manual — it saves itself here — then come back after the games
            with the DK standings.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => <SlateRow key={r.slate_dg} row={r} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
